import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const AdminDocumentsTab = ({ user }) => {
  const { tokenManager } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  // Document management states from AdminEmployeeManagement
  const [selectedCountry, setSelectedCountry] = useState('');
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [processDocuments, setProcessDocuments] = useState([]);
  const [loadingProcessDocuments, setLoadingProcessDocuments] = useState(false);
  const [loadingDocumentRequirements, setLoadingDocumentRequirements] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // HR Documents states
  const [hrDocuments, setHrDocuments] = useState([]);
  const [loadingHrDocuments, setLoadingHrDocuments] = useState(false);
  const [hrUploadDialogOpen, setHrUploadDialogOpen] = useState(false);
  const [selectedHrDocumentType, setSelectedHrDocumentType] = useState('');
  const [hrFile, setHrFile] = useState(null);
  const [uploadingHr, setUploadingHr] = useState(false);

  // Available countries for selection
  const availableCountries = [
    { value: 'India', label: 'India' },
    { value: 'Vietnam', label: 'Vietnam' },
    { value: 'Indonesia', label: 'Indonesia' },
  ];

  // HR Document types
  const HR_DOCUMENT_TYPES = {
    NDA: {
      name: 'NDA',
      module: 'Process Document',
      subModule: 'Hr Document',
      description: 'Non-Disclosure Agreement'
    },
    APPOINTMENT_LETTER: {
      name: 'Appointment Letter',
      module: 'Process Document',
      subModule: 'Hr Document',
      description: 'Official appointment letter'
    },
    KRI_KPA: {
      name: 'KRI/KPA',
      module: 'Process Document',
      subModule: 'Hr Document',
      description: 'Key Result Indicators/Key Performance Areas'
    }
  };

  // Fetch documents for the selected user
  useEffect(() => {
    if (user?.id || user?._id) {
      fetchDocuments();
    }
  }, [user]);

  // Fetch document requirements when country changes
  useEffect(() => {
    if (selectedCountry) {
      fetchDocumentRequirements(selectedCountry);
    }
  }, [selectedCountry]);

  // Fetch process documents when user and country are selected
  useEffect(() => {
    if (user && selectedCountry) {
      fetchProcessDocuments();
    }
  }, [user, selectedCountry]);

  // Fetch HR documents when user is selected
  useEffect(() => {
    if (user) {
      fetchHrDocuments();
    }
  }, [user]);

  const fetchDocuments = async () => {
    if (!user?.id && !user?._id) return;

    setLoadingDocuments(true);
    try {
      const userId = user._id || user.id;
      
      // Fetch from Employee Document submodule
      const employeeQueryParams = new URLSearchParams();
      employeeQueryParams.append('module', 'Process Document');
      employeeQueryParams.append('subModule', 'Employee Document');
      employeeQueryParams.append('userId', userId);

      const employeeResponse = await fetch(`${API_URL}/documents/user?${employeeQueryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      // Fetch from HR Document submodule
      const hrQueryParams = new URLSearchParams();
      hrQueryParams.append('module', 'Process Document');
      hrQueryParams.append('subModule', 'Hr Document');
      hrQueryParams.append('userId', userId);

      const hrResponse = await fetch(`${API_URL}/documents/user?${hrQueryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      // Fetch from Existing Document submodule
      const existingQueryParams = new URLSearchParams();
      existingQueryParams.append('module', 'Process Document');
      existingQueryParams.append('subModule', 'Existing Document');
      existingQueryParams.append('userId', userId);

      const existingResponse = await fetch(`${API_URL}/documents/user?${existingQueryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      let allDocuments = [];

      if (employeeResponse.ok) {
        const employeeResult = await employeeResponse.json();
        allDocuments = [...(employeeResult.documents || [])];
      }

      if (hrResponse.ok) {
        const hrResult = await hrResponse.json();
        allDocuments = [...allDocuments, ...(hrResult.documents || [])];
      }

      if (existingResponse.ok) {
        const existingResult = await existingResponse.json();
        allDocuments = [...allDocuments, ...(existingResult.documents || [])];
      }

      console.log('Fetched all documents for user:', user.firstName, allDocuments);
      setDocuments(allDocuments);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchDocumentRequirements = async (country) => {
    setLoadingDocumentRequirements(true);
    try {
      const response = await fetch(`${API_URL}/document-requirements/country/${country}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setDocumentRequirements(result.requirements || []);
      } else {
        console.warn('Failed to fetch document requirements from API');
        setDocumentRequirements([]);
      }
    } catch (error) {
      console.error('Error fetching document requirements:', error);
      setDocumentRequirements([]);
    } finally {
      setLoadingDocumentRequirements(false);
    }
  };

  const fetchProcessDocuments = async () => {
    if (!user) return;

    setLoadingProcessDocuments(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('module', 'Process Document');
      queryParams.append('subModule', 'Employee Document');
      queryParams.append('userId', user._id || user.id);

      const response = await fetch(`${API_URL}/documents/user?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setProcessDocuments(result.documents || []);
      }
    } catch (error) {
      console.error('Error fetching process documents:', error);
    } finally {
      setLoadingProcessDocuments(false);
    }
  };

  const fetchHrDocuments = async () => {
    if (!user) return;

    setLoadingHrDocuments(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('module', 'Process Document');
      queryParams.append('subModule', 'Hr Document');
      queryParams.append('userId', user._id || user.id);

      const response = await fetch(`${API_URL}/documents/user?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setHrDocuments(result.documents || []);
      }
    } catch (error) {
      console.error('Error fetching HR documents:', error);
    } finally {
      setLoadingHrDocuments(false);
    }
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file extension - allow PDF and image files
      const fileName = selectedFile.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('Only PDF and image files (JPG, JPEG, PNG, GIF, WEBP) are allowed. Please convert your document to one of these formats and try again.');
        setFile(null);
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      // Check if selected document type requires image only
      if (selectedDocumentType) {
        const documentConfig = documentRequirements.find(doc => doc.id === selectedDocumentType);
        if (documentConfig && documentConfig.imageOnly && fileExtension === 'pdf') {
          toast.error(`${documentConfig.label} must be an image file. PDF is not allowed for this document type.`);
          setFile(null);
          e.target.value = '';
          return;
        }
      }
      
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (selectedFile.size > maxSize) {
        toast.error('File size too large. Maximum file size is 10MB.');
        setFile(null);
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleProcessUpload = async () => {
    if (!file || !selectedDocumentType || !user) {
      toast.error('Please select file, document type, and user');
      return;
    }

    const documentConfig = documentRequirements.find(
      doc => doc.id === selectedDocumentType
    );
    
    if (!documentConfig) {
      toast.error('Invalid document type selected');
      return;
    }

    // Check if document already exists
    const existingDoc = processDocuments.find(doc => doc.documentName === documentConfig.label);
    if (existingDoc) {
      toast.error('This document type has already been uploaded');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('module', 'Process Document');
      uploadData.append('subModule', 'Employee Document');
      uploadData.append('documentName', documentConfig.label);
      uploadData.append('description', documentConfig.description);
      uploadData.append('uploadedBy', user.email);
      uploadData.append('userId', user._id || user.id);

      const response = await fetch(`${API_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
          // Don't set Content-Type for FormData - let browser set it with boundary
        },
        body: uploadData,
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setUploadDialogOpen(false);
        setFile(null);
        setSelectedDocumentType('');
        
        // Add the new document to local state immediately
        const newDocument = result.document;
        setProcessDocuments(prevDocs => [...prevDocs, newDocument]);
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // HR Document upload functions
  const handleHrFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        setHrFile(null);
        e.target.value = '';
        return;
      }

      // Check file extension - allow PDF and image files
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('Only PDF and image files are allowed');
        setHrFile(null);
        e.target.value = '';
        return;
      }

      setHrFile(selectedFile);
    }
  };

  const handleHrUpload = async () => {
    if (!hrFile || !selectedHrDocumentType || !user) {
      toast.error('Please select file, document type, and user');
      return;
    }

    if (!user || !user.email) {
      toast.error('User data is missing. Please select a user again.');
      return;
    }

    const documentConfig = HR_DOCUMENT_TYPES[selectedHrDocumentType];
    
    if (!documentConfig) {
      toast.error('Invalid document type selected');
      return;
    }

    // Check if document already exists
    const existingDoc = hrDocuments.find(doc => doc.documentName === documentConfig.name);
    if (existingDoc) {
      toast.error('This document type has already been uploaded');
      return;
    }

    setUploadingHr(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', hrFile);
      uploadData.append('module', documentConfig.module);
      uploadData.append('subModule', documentConfig.subModule);
      uploadData.append('documentName', documentConfig.name);
      uploadData.append('description', documentConfig.description || '');
      uploadData.append('uploadedBy', user.email);
      uploadData.append('userId', user._id || user.id);

      const response = await fetch(`${API_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
          // Don't set Content-Type for FormData - let browser set it with boundary
        },
        body: uploadData,
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('HR Document uploaded successfully');
        setHrUploadDialogOpen(false);
        setHrFile(null);
        setSelectedHrDocumentType('');
        
        // Add the new document to local state immediately
        const newDocument = result.document;
        setHrDocuments(prevDocs => [...prevDocs, newDocument]);
      } else {
        console.error('HR Upload failed:', result);
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploadingHr(false);
    }
  };

  const handleViewDocument = (doc) => {
    setCurrentDocument(doc);
    setDocumentOpen(true);
  };

  const handleCloseDocument = () => {
    setDocumentOpen(false);
    setCurrentDocument(null);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const fileName = doc.filePath || doc.fileName;
      
      if (!fileName) {
        toast.error('File not found');
        return;
      }

      // Show downloading toast
      toast.info(`Downloading ${doc.documentName || fileName}...`);

      // Fetch the file with authorization header first
      const response = await fetch(`${API_URL}/documents/download/${fileName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Authentication failed. Please login again.');
        } else {
          toast.error('Download failed');
        }
        return;
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Determine file extension from original filename or content type
      let fileExtension = '';
      if (fileName.includes('.')) {
        fileExtension = fileName.split('.').pop().toLowerCase();
      } else {
        // Fallback based on content type
        const contentType = response.headers.get('content-type');
        if (contentType) {
          if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
            fileExtension = 'jpg';
          } else if (contentType.includes('image/png')) {
            fileExtension = 'png';
          } else if (contentType.includes('image/gif')) {
            fileExtension = 'gif';
          } else if (contentType.includes('image/webp')) {
            fileExtension = 'webp';
          } else if (contentType.includes('application/pdf')) {
            fileExtension = 'pdf';
          }
        }
      }
      
      // Get the filename from Content-Disposition header (backend handles proper formatting)
      let downloadName = doc.documentName || fileName;
      
      // Try to get the formatted filename from the response header
      const contentDisposition = response.headers.get('content-disposition');
      
      if (contentDisposition) {
        // Extract filename from Content-Disposition header
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          downloadName = filenameMatch[1];
        }
      }
      
      // If no proper filename from header, fallback to document name with extension
      if (!contentDisposition || !contentDisposition.includes('filename=')) {
        if (fileExtension && !downloadName.toLowerCase().endsWith(`.${fileExtension}`)) {
          downloadName += `.${fileExtension}`;
        }
      }
      
      // Create a download link with unique identifier
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      link.id = `download-link-${Date.now()}`; // Unique ID
      
      // Add to DOM and trigger download
      document.body.appendChild(link);
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        link.click();
        
        // Clean up after download starts
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          window.URL.revokeObjectURL(url);
        }, 500); // Increased delay for Chrome
      });

    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (documentId) => {
    try {
      const response = await fetch(`${API_URL}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        // Remove from local state
        setDocuments((prev) => prev.filter((doc) => (doc.documentId || doc.id) !== documentId));
        toast.success('Document deleted successfully!');
      } else {
        const result = await response.json();
        toast.error(result.error || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'verified':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Verified"
            color="success"
            size="small"
          />
        );
      case 'pending':
        return (
          <Chip
            icon={<WarningIcon />}
            label="Pending"
            color="warning"
            size="small"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Employee Document Management - {user?.firstName} {user?.lastName}
      </Typography>

      {/* Country Selection */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Select Country for Process Documents
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} key="country-select">
            <FormControl fullWidth>
              <InputLabel>Select Country</InputLabel>
              <Select
                value={selectedCountry}
                onChange={handleCountryChange}
                label="Select Country"
              >
                {availableCountries.map((country) => (
                  <MenuItem key={country.value} value={country.value}>
                    {country.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Process Documents Step - Country Specific Documents */}
      {selectedCountry && documentRequirements.length > 0 && (
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '20px', fontWeight: 600 }}>
            Process Documents - {selectedCountry} Requirements
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '16px' }}>
            Upload the required documents for {selectedCountry}. Please ensure all mandatory documents are uploaded.
          </Typography>

          {/* Document Requirements */}
          <Alert severity="info" sx={{ mb: 3, '& .MuiAlert-icon': { alignSelf: 'flex-start', mt: 0.5 } }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '18px', fontWeight: 600 }}>
              Documents for {selectedCountry}:
            </Typography>
            {loadingDocumentRequirements ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={30} />
              </Box>
            ) : documentRequirements.length > 0 ? (
              <Grid container spacing={2}>
                {documentRequirements.map((docType) => (
                  <Grid item xs={12} sm={6} key={docType.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={processDocuments.some(doc => doc.documentName === docType.label)}
                        disabled
                        color="primary"
                      />
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '16px' }}>
                          {docType.label}
                          {docType.required && (
                            <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '14px' }}>
                          {docType.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No document requirements found for {selectedCountry}. Please add document requirements in the admin panel.
              </Typography>
            )}
          </Alert>

          {/* Upload Button */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              disabled={documentRequirements.length === 0 || loadingDocumentRequirements}
              sx={{ fontSize: '14px', fontWeight: 600 }}
            >
              Upload Process Document
            </Button>
          </Box>

          {/* Uploaded Documents */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '18px', fontWeight: 600 }}>
              Uploaded Documents
            </Typography>
            {loadingProcessDocuments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : processDocuments.length === 0 ? (
              <Alert severity="info">
                No documents uploaded yet for {selectedCountry} requirements.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {processDocuments.map((doc) => (
                  <Grid item xs={12} sm={6} md={4} key={doc.documentId || doc.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {doc.documentName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Uploaded: {(doc.uploadedAt || doc.uploadDate || doc.createdAt) 
                            ? new Date(doc.uploadedAt || doc.uploadDate || doc.createdAt).toLocaleDateString()
                            : 'Unknown'
                          }
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(doc)}
                          sx={{ fontSize: '14px', fontWeight: 600 }}
                        >
                          Download
                        </Button>
                        <Button
                          startIcon={<DeleteIcon />}
                          color="error"
                          onClick={() => handleDelete(doc.documentId || doc.id)}
                          sx={{ fontSize: '14px', fontWeight: 600 }}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Card>
      )}

      {/* Employee Documents Section */}
      {user && (
        <Card sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 600, mb: 1 }}>
                Employee Documents - {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: '16px' }}>
              This document is uploaded by the HR.
              </Typography>

            </Box>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setHrUploadDialogOpen(true)}
              sx={{ fontSize: '14px', fontWeight: 600 }}
            >
              Upload Employee Document
            </Button>
          </Box>

          {loadingHrDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : hrDocuments.length > 0 ? (
            <Grid container spacing={2}>
              {hrDocuments.map((doc) => (
                <Grid item xs={12} sm={6} md={4} key={doc._id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle1" sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                          {doc.documentName}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px', mb: 1 }}>
                        Uploaded: {(doc.createdAt || doc.uploadedAt || doc.uploadDate) 
                          ? new Date(doc.createdAt || doc.uploadedAt || doc.uploadDate).toLocaleDateString()
                          : 'Unknown'
                        }
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                        Size: {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : 'Unknown'}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadDocument(doc)}
                        sx={{ fontSize: '14px' }}
                      >
                        Download
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(doc._id)}
                        color="error"
                        sx={{ fontSize: '14px' }}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">
              No employee documents uploaded yet. Click "Upload Employee Document" to add documents.
            </Alert>
          )}
        </Card>
      )}

      {/* Show message when no country selected */}
      {!selectedCountry && (
        <Alert severity="info">
          Please select a country to manage process documents.
        </Alert>
      )}

      {/* Upload Document Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          Upload Document
          <IconButton
            aria-label="close"
            onClick={() => setUploadDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={selectedDocumentType}
                onChange={(e) => setSelectedDocumentType(e.target.value)}
                label="Document Type"
                disabled={documentRequirements.length === 0}
              >
                {documentRequirements.length > 0 ? (
                  documentRequirements.map((docType) => (
                    <MenuItem key={docType.id} value={docType.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">
                          {docType.label}
                        </Typography>
                        {docType.required && (
                          <Chip label="Required" size="small" color="error" />
                        )}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No document requirements available
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <input
                accept={
                  selectedDocumentType && 
                  documentRequirements.find(doc => doc.id === selectedDocumentType)?.imageOnly
                    ? "image/*"
                    : ".pdf,.jpg,.jpeg,.png,.gif,.webp"
                }
                style={{ display: 'none' }}
                id="document-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="document-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  Choose PDF or Image File
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {selectedDocumentType && 
                  documentRequirements.find(doc => doc.id === selectedDocumentType)?.imageOnly
                    ? "Accepted formats: JPG, JPEG, PNG, GIF, WEBP (Images only). Maximum file size: 10MB"
                    : "Accepted formats: PDF, JPG, JPEG, PNG, GIF, WEBP. Maximum file size: 10MB"
                }
              </Typography>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleProcessUpload}
            variant="contained"
            disabled={uploading || !file || !selectedDocumentType}
          >
            {uploading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Uploading...
              </>
            ) : (
              'Upload Document'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee Upload Document Dialog */}
      <Dialog
        open={hrUploadDialogOpen}
        onClose={() => setHrUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          Upload Employee Document
          <IconButton
            aria-label="close"
            onClick={() => setHrUploadDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Employee Document Type</InputLabel>
              <Select
                value={selectedHrDocumentType}
                onChange={(e) => setSelectedHrDocumentType(e.target.value)}
                label="Employee Document Type"
              >
                {Object.entries(HR_DOCUMENT_TYPES).map(([key, docType]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontSize: '16px' }}>
                        {docType.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <input
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                style={{ display: 'none' }}
                id="hr-document-file"
                type="file"
                onChange={handleHrFileChange}
              />
              <label htmlFor="hr-document-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ fontSize: '14px' }}
                >
                  Choose PDF or Image File
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: '12px' }}>
                Accepted formats: PDF, JPG, JPEG, PNG, GIF, WEBP. Maximum file size: 10MB
              </Typography>
              {hrFile && (
                <Typography variant="body2" sx={{ mt: 1, fontSize: '14px' }}>
                  Selected: {hrFile.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHrUploadDialogOpen(false)} sx={{ fontSize: '14px' }}>Cancel</Button>
          <Button
            onClick={handleHrUpload}
            variant="contained"
            disabled={uploadingHr || !hrFile || !selectedHrDocumentType}
            sx={{ fontSize: '14px' }}
          >
            {uploadingHr ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Uploading...
              </>
            ) : (
              'Upload Employee Document'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDocumentsTab;
